import { Badge, Box, Flex, IconButton, Image, Text, Tooltip } from '@elkaso-app/web-design';
import type { RestaurantInquiryItem } from 'apis/restaurant-inquiries';
import { describeUnits } from 'pages/inquiry-processor/utils/describe-units';
import { packagingSpecLabel, packagingSpecOf } from 'pages/inquiry-processor/utils/packaging-spec';
import { FiBox, FiEdit2 } from 'react-icons/fi';

interface RowProps {
  label: string;
  value?: string | null;
  flexible?: boolean;
  // Specific values the restaurant would also accept for this attribute — a
  // narrower, more useful signal than the generic "open to switch" once the
  // wizard has actually collected them.
  alternatives?: string[];
}

const Row = ({ label, value, flexible, alternatives }: RowProps) => {
  const altList = alternatives?.filter((a) => a?.trim());
  return (
    <Flex gap='2' fontSize='sm' align='baseline' py='0.5' wrap='wrap'>
      <Text w='56px' flexShrink={0} color='gray.500' fontSize='10px' textTransform='uppercase' letterSpacing='wide'>
        {label}
      </Text>
      <Flex align='center' gap='1.5' wrap='wrap' flex='1' minW='0'>
        <Text as='span'>{value || '—'}</Text>
        {flexible && altList?.length ? (
          <>
            <Text as='span' color='gray.500' fontSize='xs'>
              also OK:
            </Text>
            {altList.map((alt, index) => (
              <Badge
                key={`${alt}-${index}`}
                colorScheme='blue'
                variant='subtle'
                fontSize='2xs'
                textTransform='none'
                fontWeight='normal'>
                {alt}
              </Badge>
            ))}
          </>
        ) : flexible && value ? (
          <Text as='span' color='gray.500' fontSize='xs'>
            (open to switch)
          </Text>
        ) : null}
      </Flex>
    </Flex>
  );
};

interface RequestedProductPanelProps {
  item: RestaurantInquiryItem;
  // Inline data-fix loop: edit the inquiry item (volume/prices/flexibility)…
  onEditItem?: () => void;
  // …or the master product itself (brand/units/spec — what auto-quotation
  // matches against). Pencils render only when the callbacks are provided.
  onEditMaster?: () => void;
}

// The restaurant's request, in full. Missing values render as an explicit '—'
// so the operator can SEE the master-data gaps that broke auto-quotation.
export const RequestedProductPanel = ({ item, onEditItem, onEditMaster }: RequestedProductPanelProps) => {
  const mp = item.masterProduct;
  const period = item.volumePeriod ? ` / ${String(item.volumePeriod).toLowerCase().replace(/_/g, ' ')}` : '';
  return (
    <Box>
      <Flex gap='3' mb='2' align='center'>
        {mp?.imageUrl ? (
          <Image
            src={mp.imageUrl}
            alt={mp?.name || 'Product'}
            boxSize='48px'
            objectFit='cover'
            borderRadius='md'
            border='1px solid'
            borderColor='gray.200'
          />
        ) : (
          <Flex
            boxSize='48px'
            borderRadius='md'
            border='2px dashed'
            borderColor='gray.300'
            align='center'
            justify='center'
            flexShrink={0}>
            <Text fontSize='9px' color='gray.400'>
              No image
            </Text>
          </Flex>
        )}
        <Text fontWeight='semibold' flex='1' minW='0'>
          {mp?.name || `Product #${item.masterProductId}`}
        </Text>
        {onEditItem && (
          <Tooltip label='Edit request — volume, prices, flexibility' fontSize='xs'>
            <IconButton aria-label='Edit request' size='xs' variant='ghost' icon={<FiEdit2 />} onClick={onEditItem} />
          </Tooltip>
        )}
        {onEditMaster && (
          <Tooltip label='Fix master product data — brand, units, spec…' fontSize='xs'>
            <IconButton
              aria-label='Fix master product data'
              size='xs'
              variant='ghost'
              icon={<FiBox />}
              onClick={onEditMaster}
            />
          </Tooltip>
        )}
      </Flex>
      <Row
        label='Brand'
        value={mp?.brand}
        flexible={item.brandFlexible}
        alternatives={item.acceptableAlternatives?.brand}
      />
      <Row
        label='Origin'
        value={mp?.origin}
        flexible={item.originFlexible}
        alternatives={item.acceptableAlternatives?.origin}
      />
      <Row
        label='State'
        value={mp?.state}
        flexible={item.stateFlexible}
        alternatives={item.acceptableAlternatives?.state}
      />
      <Row
        label='Spec'
        value={mp?.spec}
        flexible={item.specFlexible}
        alternatives={item.acceptableAlternatives?.spec}
      />
      {/* Units carries the packaging flexibility rather than a separate
          Packaging row — both would describe the same four fields. */}
      <Row
        label='Units'
        value={describeUnits(mp)}
        flexible={item.packagingFlexible ?? true}
        alternatives={(item.acceptableAlternatives?.packaging ?? []).map((spec) =>
          packagingSpecLabel(packagingSpecOf(spec), mp?.baseUnit)
        )}
      />
      <Row label='Volume' value={item.volume ? `${item.volume} ${mp?.baseUnit || ''}${period}`.trim() : null} />
      <Row label='Current' value={item.currentPrice != null ? `${item.currentPrice} AED` : null} />
      <Row label='Target' value={item.expectedPrice != null ? `${item.expectedPrice} AED` : null} />
      {(item.expirationDate || item.isNearExpiry) && (
        <Row
          label='Expiry'
          value={[
            item.expirationDate
              ? new Date(item.expirationDate.slice(0, 10)).toLocaleDateString('en-AE', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })
              : null,
            item.isNearExpiry ? 'near expiry' : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        />
      )}
      {item.notes ? <Row label='Notes' value={item.notes} /> : null}
    </Box>
  );
};
